/** The census states a source's windows are in, counted and **never merged** — Batch 5.
 *
 * The site page lists many sources and the source page one; both need the same sentence about a
 * source's windows, and both must keep the six states apart (`sourceModel.ts` says why: collapsing
 * *missing* into *not expected*, or *unknown* into *submitted*, is how "we have no data" becomes
 * "the emissions were zero"). So the tally is derived here from `windows`, which is the census's own
 * answer, and not from the summary counts on the row: those five counts omit `not_expected`, and a
 * tally that left a state out would read as the state not existing.
 *
 * A state with no window is not drawn: six chips of which five read zero would bury the one that
 * matters. Each chip carries its word and its glyph (a state must not depend on colour), and
 * `data-state` so a test asserts the fact and not the hue.
 */

import type { SourceWindow } from "../api/sources";
import { STATE, STATE_ORDER, countStates } from "./sourceModel";
import { StateGlyph } from "./sourceParts";
import styles from "./DetailPage.module.css";

export function StateTally({ windows, testId }: { windows: readonly SourceWindow[]; testId?: string }) {
  if (windows.length === 0) {
    return <span className={styles.rowMeta}>Nothing is expected in this period.</span>;
  }
  const counts = countStates(windows);
  // A state this build does not know is still drawn, after the six: dropping it would hide a
  // seventh state behind the six we named.
  const extra = Object.keys(counts).filter((state) => !(STATE_ORDER as readonly string[]).includes(state));
  return (
    <ul className={styles.tally} data-testid={testId} aria-label="Periods by expected-bill state">
      {[...STATE_ORDER, ...extra]
        .filter((state) => (counts[state] ?? 0) > 0)
        .map((state) => (
          <li key={state} className={styles.tallyItem} data-state={state} data-count={counts[state]}>
            <StateGlyph state={state} />
            {counts[state]} {STATE[state]?.short ?? state.replace(/_/g, " ")}
          </li>
        ))}
    </ul>
  );
}
